import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { currentTenantId } from '../../../library/brandingCache';
import { toTimeInputValue } from '../../../library/dateTimeHelper';
import { IClinicMember } from '../../../library/models/clinic';
import { IProgramDay, WEEKDAY_LABELS } from '../../../library/models/clinicProgram';
import { ITherapistProgram } from '../../../library/models/therapistProgram';
import { memberFullName } from '../../../library/membershipDisplay';
import { useIsCompact } from '../../../library/useIsCompact';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import {
    dayHoursStyles,
    dayLabelStyles,
    dayOffStyles,
    dayRowStyles,
    descriptionStyles,
    dialogActionsStyles,
    dialogTitleStyles,
    loadingBoxStyles,
    weekStyles,
} from './therapistScheduleDialog.styles';

interface TherapistScheduleDialogProps {
    /** The therapist being looked at, or null when the dialog is closed. */
    therapist: IClinicMember | null;
    onClose: () => void;
}

/**
 * When one therapist is available, for the administrator who needs to know.
 *
 * **Read-only, deliberately.** A therapist's own hours are theirs to set, the way the
 * clinic's are the administrator's; what the administrator controls is the ceiling, and they
 * control it from the Working hours tab, where narrowing the clinic narrows everybody who was
 * working past the new closing time and says whose. Giving them a second way to edit one
 * person's week would put two people in charge of the same field with no way to tell whose
 * change is on screen.
 *
 * What it shows is what is **in force** -- the clinic's hours already narrowed by the
 * therapist's own -- so it is the same week the booking screen offers patients, and not a
 * stored intention the cap may already have overtaken. The line beneath the title says which
 * of the two states it is: their own schedule, or the clinic's hours because they have set
 * none.
 */
export const TherapistScheduleDialog = (props: TherapistScheduleDialogProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const isCompact = useIsCompact();

    const isOpen: boolean = props.therapist !== null;
    const therapistId: number = props.therapist?.id ?? 0;

    const { data, isLoading, isError } = useReactQuery<ITherapistProgram>({
        ...services.therapistProgram.getProgram(therapistId, currentTenantId()),
        enabled: isOpen && therapistId > 0,
    });

    return (
        <Dialog open={isOpen} onClose={props.onClose} fullWidth maxWidth="xs" fullScreen={isCompact}>
            <DialogTitle sx={dialogTitleStyles}>
                {translate(ResourcesIds.THERAPIST_SCHEDULE_TITLE, {
                    name: props.therapist ? memberFullName(props.therapist) : '',
                })}
            </DialogTitle>

            <DialogContent>
                {isError && (
                    <Alert severity="error">
                        {translate(ResourcesIds.THERAPIST_SCHEDULE_LOAD_ERROR)}
                    </Alert>
                )}

                {isLoading ? (
                    <Box sx={loadingBoxStyles}>
                        <CircularProgress />
                    </Box>
                ) : data ? (
                    <>
                        <Typography variant="body2" sx={descriptionStyles}>
                            {translate(data.custom
                                ? ResourcesIds.THERAPIST_SCHEDULE_CUSTOM
                                : ResourcesIds.THERAPIST_SCHEDULE_CLINIC_DEFAULT)}
                        </Typography>

                        <Box sx={weekStyles}>
                            {data.days.map((day: IProgramDay) => (
                                <Box key={day.dayOfWeek} sx={dayRowStyles}>
                                    <Typography sx={dayLabelStyles}>
                                        {translate(WEEKDAY_LABELS[day.dayOfWeek])}
                                    </Typography>
                                    <Typography sx={day.open ? dayHoursStyles : dayOffStyles}>
                                        {day.open
                                            ? `${toTimeInputValue(day.opensAt)} – ${toTimeInputValue(day.closesAt)}`
                                            : translate(ResourcesIds.THERAPIST_SCHEDULE_DAY_OFF)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </>
                ) : null}
            </DialogContent>

            <DialogActions sx={dialogActionsStyles}>
                <Button onClick={props.onClose} variant="outlined">
                    {translate(ResourcesIds.CLOSE_DIALOG)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
